import { useCallback } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import type { CreateCallbackWithOptions } from '@atlassian/jira-platform-board-kit/src/ui/inline-card-create/types.tsx';
import { setMark, setMeasure } from '@atlassian/jira-common-performance/src/marks.tsx';
import {
	FOCUS_TO_CREATE_END_MARK,
	FOCUS_TO_CREATE_MEASURE_NAME,
	FOCUS_TO_CREATE_START_MARK,
} from '../constants.tsx';

type Args = {
	onChangeFormVisible: (isFormVisible: boolean) => void;
	onCreate: CreateCallbackWithOptions;
};
export const useCallbacksWrappersForPICExperimentMeasures = ({
	onChangeFormVisible,
	onCreate,
}: Args) => {
	const onChangeFormVisibleWithExperimentMeasure = fg('powerful_inline_create_software_board_check')
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useCallback(
				(isFormVisible: boolean) => {
					if (isFormVisible) {
						// Whenever this form is made visible, set a mark to measure the delay between form open and onCreate
						// being called. This is to quantify the experience between seeing the form and creating an issue
						setMark(FOCUS_TO_CREATE_START_MARK);
					} else {
						// Whenever this form is closed, clear the mark, as the experience is over
						// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
						performance.clearMarks(FOCUS_TO_CREATE_START_MARK);
					}
					onChangeFormVisible(isFormVisible);
				},
				[onChangeFormVisible],
			)
		: onChangeFormVisible;
	const onCreateWithExperimentMeasure: CreateCallbackWithOptions = fg(
		'powerful_inline_create_software_board_check',
	)
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useCallback<CreateCallbackWithOptions>(
				(partialIssues) => {
					// On create is the end of the ICC experience, so we set the end mark, measure the delay, log the results (in onCreateWithEventAttributes)
					// and at the end clear the marks and measures, so it can start over
					setMark(FOCUS_TO_CREATE_END_MARK);
					setMeasure(
						FOCUS_TO_CREATE_MEASURE_NAME,
						FOCUS_TO_CREATE_START_MARK,
						FOCUS_TO_CREATE_END_MARK,
					);
					// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
					const measurePerformanceEntries = performance.getEntriesByName(
						FOCUS_TO_CREATE_MEASURE_NAME,
					);
					const mostRecentMeasure = measurePerformanceEntries.at(-1) ?? null;

					onCreate(partialIssues, {
						issueCreatedAnalyticsEventAttributes: {
							timeMsSinceFocus: mostRecentMeasure && Math.round(mostRecentMeasure.duration),
						},
					});

					// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
					performance.clearMarks(FOCUS_TO_CREATE_END_MARK);
					// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
					performance.clearMeasures(FOCUS_TO_CREATE_MEASURE_NAME);
				},
				[onCreate],
			)
		: onCreate;

	return { onCreateWithExperimentMeasure, onChangeFormVisibleWithExperimentMeasure };
};
